import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

// Custom SVG illustrations per category
const CategoryArt = ({ category }) => {
  const palette = {
    "Product Management":   { a: "#60a5fa", b: "#818cf8" },
    "IoT & Hardware":       { a: "#22d3ee", b: "#38bdf8" },
    "Artificial Intelligence":{ a: "#a78bfa", b: "#60a5fa" },
    "Smart Energy":         { a: "#fbbf24", b: "#a78bfa" },
    "Product Strategy":     { a: "#60a5fa", b: "#22d3ee" },
    "Community":            { a: "#34d399", b: "#22d3ee" },
  };
  const { a, b } = palette[category] || { a: "#60a5fa", b: "#a78bfa" };

  return (
    <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`bg-${category}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={a} stopOpacity="0.2" />
          <stop offset="100%" stopColor={b} stopOpacity="0.05" />
        </linearGradient>
        <radialGradient id={`glow-${category}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={a} stopOpacity="0.5" />
          <stop offset="100%" stopColor={a} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="200" height="100" fill={`url(#bg-${category})`} />
      {/* grid */}
      <g stroke="rgba(148,163,184,0.07)" strokeWidth="0.4">
        {Array.from({ length: 10 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="100" />
        ))}
        {Array.from({ length: 6 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 20} x2="200" y2={i * 20} />
        ))}
      </g>
      {/* glow */}
      <circle cx="100" cy="50" r="50" fill={`url(#glow-${category})`} />
      {/* dynamic shapes per category */}
      {category === "IoT & Hardware" && (
        <>
          {Array.from({ length: 6 }).map((_, i) => (
            <circle key={i} cx={30 + i * 28} cy={50} r="1.6" fill={a}>
              <animate attributeName="opacity" values="0.3;1;0.3" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <path d="M30 50 L58 50 L86 50 L114 50 L142 50 L170 50" stroke={b} strokeWidth="0.5" />
        </>
      )}
      {category === "Artificial Intelligence" && (
        <>
          <circle cx="100" cy="50" r="14" fill="none" stroke={a} strokeWidth="0.6" />
          <circle cx="100" cy="50" r="22" fill="none" stroke={b} strokeWidth="0.5" strokeDasharray="2 3" />
          {[0, 90, 180, 270].map((deg) => (
            <circle key={deg} cx={100 + 22 * Math.cos((deg * Math.PI) / 180)} cy={50 + 22 * Math.sin((deg * Math.PI) / 180)} r="2" fill={a} />
          ))}
        </>
      )}
      {category === "Smart Energy" && (
        <>
          <path d="M70 70 L95 35 L88 50 L115 50 L90 80 L98 60 L78 60 Z" fill={a} opacity="0.8" />
        </>
      )}
      {category === "Product Management" && (
        <>
          {[20, 50, 80].map((y, i) => (
            <rect key={i} x="50" y={y} width={60 + i * 25} height="6" rx="3" fill={a} opacity={0.4 + i * 0.2} />
          ))}
        </>
      )}
      {category === "Product Strategy" && (
        <>
          <polyline points="30,75 60,55 95,65 130,30 170,40" fill="none" stroke={a} strokeWidth="1" />
          {[[30, 75], [60, 55], [95, 65], [130, 30], [170, 40]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.8" fill={b} />
          ))}
        </>
      )}
      {category === "Community" && (
        <>
          {[[60, 50], [100, 40], [140, 50], [85, 70], [115, 70]].map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="4" fill={a} opacity="0.7" />
            </g>
          ))}
          <path d="M60 50 L100 40 L140 50 L115 70 L85 70 Z" stroke={b} strokeWidth="0.5" fill="none" />
        </>
      )}
    </svg>
  );
};

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles?limit=6`);
        if (mounted) setArticles(data || []);
      } catch (e) {
        console.error("Failed to load articles", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <section id="blog" data-testid="blog-section" className="relative py-24 lg:py-32 bg-section-tint">
      <div className="absolute inset-0 grid-bg-fine opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">
              <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
              /// Insight & Article
            </div>
            <h2
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
              data-testid="blog-title"
            >
              Pemikiran tentang{" "}
              <span className="text-gradient-aurora">produk, teknologi, dan ekosistem.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-2">
            <p className="text-base text-slate-400 leading-relaxed">
              Catatan, refleksi, dan studi kasus dari pengalaman membangun produk teknologi, AI, IoT,
              dan platform digital berskala nasional.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl glass h-80 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
            {articles.map((a, idx) => (
              <motion.article
                key={a.id}
                data-testid={`blog-card-${idx}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (idx % 3) * 0.07 }}
                className="group relative overflow-hidden"
              >
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-link-${idx}`}
                  className="relative flex flex-col rounded-2xl p-5 glass-elevated corners scanline-hover hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden h-full"
                >
                {/* cover */}
                <div className="relative h-40 rounded-xl mb-5 overflow-hidden border border-white/[0.05]">
                  <CategoryArt category={a.category} />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] bg-[#060a1a]/80 backdrop-blur border border-white/10 text-blue-200 font-mono-display">
                    {a.category}
                  </span>
                  <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#060a1a]/80 backdrop-blur border border-white/10 grid place-items-center text-blue-300/80 text-sm">
                    {a.cover_emoji}
                  </span>
                </div>

                <h3
                  className="text-lg text-white font-medium leading-snug group-hover:text-gradient-aurora transition-all"
                  style={{ fontFamily: "Outfit" }}
                >
                  {a.title}
                </h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed line-clamp-3">
                  {a.excerpt}
                </p>

                <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs text-slate-500 font-mono-display">
                  <div className="flex items-center gap-3">
                    <span>{formatDate(a.published_at)}</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" strokeWidth={1.75} />
                      {a.read_time}
                    </span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-blue-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Blog;
